
import {QDrawer, QItemSection, QScrollArea} from "quasar"
import Component from "vue-class-component"
import Vue from "vue"
import {Inject, Prop} from "vue-property-decorator"
import {mixin as clickaway} from "vue-clickaway"
import {superSubmenuOptions} from "@/superannuation/superSubmenuOptions"
import {staffSubmenuOptions} from "@/staff/staffSubmenuOptions"
import MenuItem from "@/layout/MenuItem.vue"
import {AuthenticationService} from "@/AuthenticationService"
import {ActuatorService} from "@/ActuatorService"

declare const APP_VERSION: string

@Component({
  mixins: [clickaway],
  components: {QDrawer, QScrollArea, QItemSection, MenuItem},
})
export default class Sidebar extends Vue {
  @Prop()
  value: boolean

  @Inject()
  layout: any

  @Inject()
  authenticationService: AuthenticationService

  @Inject()
  actuatorService: ActuatorService

  isSubmenuOpen = false
  isOpen = false // local mirror of the `value` prop — never mutate the prop directly
  menuOption = MenuOption
  superSubmenuOptions = superSubmenuOptions
  staffSubmenuOptions = staffSubmenuOptions
  submenuItemList: MenuOption = <any>null
  activeMenuItem: string | null = <any>null
  isAnimationEnabled = false

  isStaff = false
  isAdmin = false
  isSuper = false
  appVersion = APP_VERSION
  revision: string | null = null

  async created() {
    this.isStaff = await this.authenticationService.isStaff()
    this.isAdmin = await this.authenticationService.isAdmin()
    this.isSuper = await this.authenticationService.isSuper()

    const actuatorInfo = await this.actuatorService.getInfo()
    this.revision = actuatorInfo.build?.number ?? null
  }

  get versionLabel(): string {
    return this.revision !== null && this.revision !== ""
      ? `v${this.appVersion}r${this.revision}`
      : `v${this.appVersion}`
  }

  async mounted() {
    this.isOpen = this.value
    // Sync parent → local
    this.$watch(
      () => this.value,
      n => {
        this.isOpen = n
      }
    )
    // Sync local → parent and handle side effects
    this.$watch(
      () => this.isOpen,
      async n => {
        this.$emit("input", n)
        if (n && !this.isSubmenuOpen) this.setActiveMenuItem(null)
        if (!n) this.closeSubmenu()
      }
    )
    // Prevent animation on initial load
    this.isAnimationEnabled = true
  }

  get allowedStaffSubmenuOptions() {
    return staffSubmenuOptions.filter(
      option => (this.isAdmin || !option.admin) && (this.isSuper || !option.super)
    )
  }

  updateSubmenu(name: string, menuItem?: MenuOption) {
    if (!this.isSubmenuOpen && menuItem !== undefined) {
      this.isSubmenuOpen = true
      this.submenuItemList = menuItem
      this.setActiveMenuItem(name)
    } else if (this.isSubmenuOpen && menuItem !== undefined) {
      if (this.submenuItemList === menuItem) {
        this.closeSubmenu()
      } else {
        this.submenuItemList = menuItem
        this.setActiveMenuItem(name)
      }
    } else if (this.isSubmenuOpen) {
      this.closeSubmenu()
    }
  }

  setActiveMenuItem(name: string | null) {
    this.activeMenuItem = name
  }

  closeSubmenu() {
    this.isSubmenuOpen = false
    this.setActiveMenuItem(null)
  }

  private get contentStyle() {
    // On iOS the content is always fixed (see Quasar docs), so force a overflow
    // for the submenu to work, however it needs to be removed in order for the
    // main menu scrolling to work
    return this.$q.platform.is.ios === true && this.isSubmenuOpen ? "overflow: visible" : ""
  }
}

enum MenuOption {
  Dashboard = 1,
  Super,
  Staff,
  Benefits,
  DocsVault,
  Surveys,
  Info,
}
